import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminApiDomain, getAdminApiUrl } from '../../../config/ApiRouting';
import type { PackingInvoice } from '../../../infrastructure/FulfillmentAdminClient';
import { FulfillmentAdminClient } from '../../../infrastructure/FulfillmentAdminClient';
import { PrintDispatcher, PrintFailureNotice } from '@/pages/fulfillment/components/PrintDispatcher';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { LABEL_PAGE } from '@/pages/fulfillment/components/labelSheet';
import { PackingSheet } from '@/pages/fulfillment/components/PackingSheet';
import './packingInvoice.css';

/**
 * The PACKING sheet behind Order Detail, shown as the label printer will print it: the same document View
 * Orders prints, drawn by the one renderer, so the two can never drift apart again [SSD-7207].
 */
export default function PackingInvoicePrintPage(): JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const [invoice, setInvoice] = useState<PackingInvoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toasts, showError, dismiss } = useFulfillmentToast();

  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  const printer = useMemo(() => new PrintDispatcher(), []);

  /**
   * Printing on load put paper through the tray for anyone who merely opened the route. The invoice is
   * re-read here because a sheet left open on the bench may have been amended since [SSD-6352].
   */
  const print = async () => {
    let html: string;
    try {
      const fresh = await client.getPackingInvoice(Number(orderId));
      html = PackingSheet.htmlOf(fresh);
      setInvoice(fresh);
    } catch (cause) {
      showError((cause as Error).message);
      return;
    }
    const outcome = await printer.dispatch({
      surface: 'packingInvoice',
      jobKey: `packingInvoice:${orderId}`,
      role: 'label',
      format: 'html',
      html,
    });
    if (!outcome.ok) {
      showError(PrintFailureNotice.textOf(outcome));
    }
  };

  useEffect(() => {
    if (!orderId) {
      return;
    }
    client
      .getPackingInvoice(Number(orderId))
      .then(setInvoice)
      .catch((cause: Error) => setError(cause.message));
  }, [client, orderId]);

  // The document itself is the preview: what the frame shows is byte for byte what the printer gets.
  const sheet = useMemo(() => {
    if (!invoice) {
      return null;
    }
    try {
      return { html: PackingSheet.htmlOf(invoice), pages: PackingSheet.pagesOf(invoice).length };
    } catch (cause) {
      return { error: (cause as Error).message };
    }
  }, [invoice]);

  if (error) {
    return <div className="packing-invoice-error">{error}</div>;
  }
  if (!invoice || !sheet) {
    return <div className="packing-invoice-loading">Loading…</div>;
  }
  if ('error' in sheet) {
    return <div className="packing-invoice-error">{sheet.error}</div>;
  }

  return (
    <div className="packing-invoice">
      <div className="packing-invoice-actions">
        <button type="button" className="btn btn-blue btn-sm" onClick={() => void print()}>
          🖨 Print
        </button>
        <span className="packing-invoice-hint">
          PACKING #{invoice.orderId} · {sheet.pages} label{sheet.pages === 1 ? '' : 's'} of {LABEL_PAGE.widthMm}×
          {LABEL_PAGE.heightMm} mm
        </span>
      </div>
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
      <iframe
        className="packing-invoice-preview"
        title={`PACKING #${invoice.orderId}`}
        data-testid="invoice-preview"
        srcDoc={sheet.html}
        style={{ height: `calc(${sheet.pages} * ${LABEL_PAGE.heightMm}mm + ${sheet.pages * 6}px)` }}
      />
    </div>
  );
}
